import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AudioLines, ClipboardCheck, ShieldCheck, Eye, EyeOff, ArrowRight, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import LoadingSpinner from '../common/LoadingSpinner';
import LanguageSwitcher from '../common/LanguageSwitcher';
import Brand, { BrandHome } from '../common/Brand';
import ConversationRibbon from '../common/ConversationRibbon';
import AccessGeometry from './AccessGeometry';

export default function Login() {
  const { t } = useTranslation(['auth', 'common', 'brand']);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, user, profile } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user && profile) {
      if (profile.password_changed === false) {
        navigate('/force-password-change', { replace: true });
        return;
      }

      const fromParam = new URLSearchParams(location.search).get('from');
      if (fromParam && fromParam.startsWith('/') && !fromParam.startsWith('/error/')) {
        navigate(fromParam, { replace: true });
        return;
      }

      if (profile.role === 'admin' || profile.role === 'super_admin') {
        navigate('/admin');
      } else if (profile.role === 'candidate') {
        navigate('/candidate');
      } else if (profile.role === 'invigilator') {
        navigate('/invigilator');
      }
    }
  }, [user, profile, navigate, location.search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { error } = await signIn(email, password);
    
    if (error) {
      setError(error.message);
    }
    
    setLoading(false);
  };

  return (
    <div className="login-experience app-shell relative">
      <div className="auth-tools">
        <LanguageSwitcher compact idPrefix="login-language" className="access-language" />
        <button type="button" onClick={toggleTheme} className="btn-ghost p-3" aria-label={t('login.toggleTheme', { ns: 'auth' })}>
          {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>
      </div>
      <div className="auth-layout">
        <section className="auth-scene auth-story">
          <ConversationRibbon />
          <AccessGeometry />
          <BrandHome />
          <Brand artwork className="auth-artwork" />
          <div>
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">{t('headline', { ns: 'brand' })}<br />{t('headlineAccent', { ns: 'brand' })}</h2>
            <p className="mt-3 text-sm text-[var(--brand-field-muted)]">Where Conversations Become Credible Assessments.</p>
          </div>
          <div className="auth-capabilities">
            {[AudioLines, ClipboardCheck, ShieldCheck].map((Icon, index) => (
              <div key={index} className="auth-capability"><Icon aria-hidden="true" /><div>
                <h3 className="text-sm font-semibold">{t(`capabilities.${index === 2 ? 4 : index}`, { ns: 'brand' })}</h3>
                <p>{t(`capabilityBodies.${index === 2 ? 4 : index}`, { ns: 'brand' })}</p>
              </div></div>
            ))}
          </div>
        </section>
        <section className="auth-form-area">
          <div className="auth-form-frame">
            <ConversationRibbon className="access-frame-wave" />
            <div className="auth-form-card">
              <div className="mb-8"><h1>{t('login.welcomeBack', { ns: 'auth' })}</h1><p className="mt-3 text-sm text-[var(--text-secondary)]">{t('login.subtitle', { ns: 'auth' })}</p></div>
              <form onSubmit={handleSubmit} className="space-y-5" aria-busy={loading}>
                {error && <div role="alert" id="login-error" className="theme-banner-danger rounded-xl px-4 py-3 text-sm">{error}</div>}
                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-semibold">{t('login.emailAddress', { ns: 'auth' })}</label>
                  <input id="email" name="email" type="email" spellCheck={false} autoComplete="username" required value={email} onChange={(event) => setEmail(event.target.value)} className="input" placeholder={t('login.emailPlaceholder', { ns: 'auth' })} aria-describedby={error ? 'login-error' : undefined} />
                </div>
                <div>
                  <label htmlFor="password" className="mb-2 block text-sm font-semibold">{t('login.password', { ns: 'auth' })}</label>
                  <div className="relative">
                    <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} className="input pr-14" placeholder={t('login.passwordPlaceholder', { ns: 'auth' })} aria-describedby={error ? 'login-error' : undefined} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="auth-visibility absolute right-1 top-1/2 -translate-y-1/2 rounded-lg p-3 text-[var(--text-secondary)]" aria-label={t(showPassword ? 'hidePassword' : 'showPassword', { ns: 'brand' })} aria-pressed={showPassword}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                  </div>
                </div>
                <button type="submit" disabled={loading} className="btn-primary flex w-full items-center justify-center gap-2 py-3.5">
                  {loading ? <LoadingSpinner size="sm" /> : <>{t('login.signIn', { ns: 'auth' })}<ArrowRight size={17} /></>}
                </button>
              </form>
              <div className="mt-6 rounded-xl border border-[var(--border-light)] bg-[var(--bg-tertiary)] px-4 py-3 text-sm text-[var(--text-secondary)]"><span className="font-semibold text-[var(--text-primary)]">{t('login.firstTimeTitle', { ns: 'auth' })}</span>{' '}{t('login.firstTimeBody', { ns: 'auth' })}</div>
              <p className="mt-5 text-xs leading-relaxed text-[var(--text-tertiary)]">{t('privacy.loginBanner', { ns: 'common' })}{' '}<Link to="/privacy-notice" className="font-semibold text-[var(--mcq-accent)] underline underline-offset-2">{t('privacy.readNotice', { ns: 'common' })}</Link></p>
              <Link to="/" className="mt-6 inline-block text-xs font-semibold text-[var(--mcq-accent)]">← {t('backHome', { ns: 'brand' })}</Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
